import { useState } from "react";

import {
  BASE_WELL_DEFAULT_ZOOM,
  BASE_WELL_LAYER,
  SHAPEFILE_DETAILS_LABEL_LAYER,
  TYPE_LOG_LAYER
} from "constants/mapLayers.constants";
import {
  selectDisplaySettings,
  selectLayers,
  selectMapZoom,
  selectWellLegendItems,
  useMapStore
} from "stores/map";
import styled from "styled-components";

import RangeInput from "components/base/RangeInput";
import { SHAPEFILE_DETAIL_DEFAULT_MIN_ZOOM } from "components/map/hooks/manage-mapbox-shapefile-loading/utils/on-shapefile-property-changed/addShapefilePropertyLayer";
import { getTypeLogZoomRange } from "components/map/utils/entitiesOnMapFetchPolicy";
import { useUpdateLayerZoom } from "components/project/layers/hooks";

export interface IZoomSliderProps {
  layerName: string;
  isShapefileLayer: boolean;
}

const DEFAULT_MIN_ZOOM = 0;
const DEFAULT_MAX_ZOOM = 22;

export function ZoomSlider({ layerName, isShapefileLayer }: IZoomSliderProps) {
  const zoom = useMapStore(selectMapZoom);
  const mapLayers = useMapStore(selectLayers) ?? [];
  const displaySettings = useMapStore(selectDisplaySettings);
  const wellLegendItems = useMapStore(selectWellLegendItems);

  const { updateLayerZoom } = useUpdateLayerZoom();

  // find the layer in the global state by id
  const mapLayer = mapLayers.find((l) => l.id === layerName);
  const zoomSettings = displaySettings.layerZoomSettings?.[layerName];
  const defaultMinZoom =
    layerName === SHAPEFILE_DETAILS_LABEL_LAYER
      ? SHAPEFILE_DETAIL_DEFAULT_MIN_ZOOM
      : layerName === BASE_WELL_LAYER
      ? BASE_WELL_DEFAULT_ZOOM.minzoom
      : DEFAULT_MIN_ZOOM;
  const defaultMaxZoom =
    layerName === BASE_WELL_LAYER ? BASE_WELL_DEFAULT_ZOOM.maxzoom : DEFAULT_MAX_ZOOM;
  const initialMin = zoomSettings?.minzoom ?? mapLayer?.minzoom ?? defaultMinZoom;
  const initialMax = zoomSettings?.maxzoom ?? mapLayer?.maxzoom ?? defaultMaxZoom;
  const [zoomRange, setZoomRange] = useState([initialMin, initialMax]);
  // Type Log's effective range moves with the well count (useTypeLogWellsLayer), so it is
  // derived live instead of from the mount-time snapshot or the layer store entry.
  const values =
    layerName === TYPE_LOG_LAYER
      ? getTypeLogZoomRange(wellLegendItems, zoomSettings)
      : zoomRange;

  const onZoomChange = (minAndMax: [number, number]) => {
    if (!mapLayers || mapLayers.length === 0) return;
    setZoomRange([...minAndMax]);
    const layerIdsToUpdate = [layerName];

    if (isShapefileLayer) {
      layerIdsToUpdate.push(
        `${layerName}-stroke`,
        `${layerName}-point`,
        `${layerName}-property`
      );
    }

    updateLayerZoom({
      mapLayers,
      minAndMax,
      layerNames: layerIdsToUpdate
    });
  };

  return (
    <>
      <LabelWrapper>
        <label>
          Zoom Level <SubText>{`Current: ${zoom.toFixed(2)}`}</SubText>
        </label>
      </LabelWrapper>

      <RangeWrapper>
        <RangeInput values={values} onChange={onZoomChange} step={1} min={0} max={22} />
      </RangeWrapper>
    </>
  );
}

const SubText = styled.div`
  font-size: 0.8em;
  line-height: 0.8em;
  color: #a2aaad;
`;

const LabelWrapper = styled.div`
  width: 100%;
`;

const RangeWrapper = styled.div`
  width: 100%;
  padding-right: 8px;
  padding-left: 8px;
`;
